// 运行时配置
import { SLS_CLIENT } from '@aliyun-sls/web-browser';
import { AxiosError, RequestConfig, getLocale } from '@umijs/max';
import { ConfigProvider, message } from 'antd';
import JSONBigInt from 'json-bigint';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { fetchInitialData } from './services/user';
import { ChannelConfig } from './services/user/typings';
import { useComponentStore, useUserStore } from './store';
import { getEmailLoginMode, jumpLandingPage } from './utils/jump';
import { API_ENV, BASE_URL, ResponseWrap } from './utils/request';

const SLS_ENV = API_ENV; // process.env.NODE_ENV === 'development' ? 'dev' : 'prod';
SLS_CLIENT.init({
  host: 'cn-shanghai.log.aliyuncs.com',
  project: 'log-service-project-knows-group-cn-shanghai',
  logstore: `medknows-web-${SLS_ENV}-raw`,
  workspace: `medknows-web-${SLS_ENV}`,
  env: SLS_ENV,
  service: 'web',
  enableRuntimeError: true,
  enableResourcePerf: true,
  enableRequest: true,
  enablePerf: true,
  enableTrace: true,
  enableConsoleLog: 'info',
});

const JSONBigIntStringAsString = JSONBigInt({ storeAsString: true });

message.config({
  maxCount: 2,
});

function isJsonString(str: any): str is string {
  if (typeof str !== 'string') {
    return false;
  }
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
const ignore = ['/', '/welcome', '/welcome/en'];

export const request: RequestConfig = {
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
  transformResponse: (resp) => {
    if (!isJsonString(resp)) {
      return resp;
    }
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/BigInt
    // https://stackoverflow.com/questions/43787712/axios-how-to-deal-with-big-integers
    // replace big int to string
    return JSONBigIntStringAsString.parse(resp);
  },
  requestInterceptors: [
    (reqConfig) => {
      const token = localStorage.getItem('token');
      const encrypted_user_id = localStorage.getItem('encrypted_user_id');
      return {
        ...reqConfig,
        headers: {
          Authorization: `Bearer ${token}`,
          ...reqConfig.headers,
          'x-encrypted-user-id': encrypted_user_id || '',
          'Accept-Language': getLocale(),
        },
      };
    },
  ],

  // 403 A4290 -> 20242 knows & mediflow // 权限使用次数上限
  // 403 A0242、A0243 -> 20243 knows & mediflow // 没有权限 A0242手机号 A0243无权限
  // 403 A0419 -> 20246 knows & mediflow // 更新渠道用户信息（弹出表单）
  //  knows错误转mediflow错误为66255 // 其他错误 已弃用

  responseInterceptors: [
    (resp) => {
      if (
        resp &&
        resp.status === 200 &&
        (resp?.data?.code === 20243 || resp?.data?.code === 20245)
      ) {
        // 没有权限
        useComponentStore.getState().onResetOpen('NoPermissionModal');
      }
      return resp;
    },
  ],

  errorConfig: {
    errorHandler: (err) => {
      const error = err as AxiosError<ResponseWrap<any>>;
      // if (error.response?.status !== 200 && error.response?.status !== 403) {
      // if (ignore.includes(location.pathname)) {
      //   jumpLandingPage();
      //   return;
      // }
      // }
      if (error.response?.status === 401) {
        sessionStorage.setItem('redirect_url', window.location.href);
        if (location.pathname.includes('/pricing')) {
          return;
        }

        if (location.pathname.includes('/file-translate')) {
          const isOpened =
            !!useComponentStore.getState().state?.LoginModal?.open;
          if (isOpened) return;
          useUserStore.getState().clearUser();
          useComponentStore.getState().onResetOpen('LoginModal', {
            closable: true,
          });
          return;
        }
        const landingPage = localStorage.getItem('third_landing_page');
        if (landingPage || ignore.includes(location.pathname)) {
          // 没有接口登录的租户，一律跳走
          jumpLandingPage();
          return;
        }
        const isOpened = !!useComponentStore.getState().state?.LoginModal?.open;
        if (isOpened) return;
        useUserStore.getState().clearUser();
        useComponentStore.getState().onResetOpen('LoginModal', {
          closable: true,
        });
      }
      if (error.response?.status === 403) {
        if (error.response?.data.code === 'A0243') {
          // knows A0243无权限
          useComponentStore.getState().onResetOpen('NoPermissionModal');
        }
        if (error.response?.data.code === 'A4290') {
          // 403 A4290 -> 20242 knows & mediflow // 权限使用次数上限
          message.error(error.response?.data.msg);
        }
        // if (error.response?.data.code === 'A0239') {
        //   message.error(error.response?.data.msg);
        // }
        if (error.response?.data.code === 'A0242') {
          // knows A0242手机号
          const isOpened =
            !!useComponentStore.getState().state?.BindPhoneModal?.open;
          if (isOpened) return;
          useComponentStore.getState().onResetOpen('BindPhoneModal', {
            keyboard: false,
            closable: false,
            maskClosable: false,
          });
          if (location.pathname === '/') {
            window.location.replace('/home');
          }
        }
        if (
          error.response?.data.code === 20246 ||
          error.response?.data.code === 'A0419'
        ) {
          // 补充用户信息
          const isOpened =
            !!useComponentStore.getState().state?.UpdateUserInfoModal?.open;
          if (isOpened) return;
          useComponentStore.getState().onResetOpen('UpdateUserInfoModal', {
            keyboard: false,
            closable: false,
            maskClosable: false,
          });
          if (location.pathname === '/') {
            window.location.replace('/home');
          }
        }
      }
    },
  },
  // TODO: responseInterceptors
};

export async function getInitialState(): Promise<ChannelConfig> {
  // 设置Referrer策略，确保Google OAuth在localhost和HTTP环境下正常工作
  // 参考：https://developers.google.com/identity/protocols/oauth2/openid-connect#referrer-policy
  if (document.head) {
    const existingMeta = document.querySelector('meta[name="referrer"]');
    if (!existingMeta) {
      const referrerMeta = document.createElement('meta');
      referrerMeta.name = 'referrer';
      referrerMeta.content = 'no-referrer-when-downgrade';
      document.head.appendChild(referrerMeta);
    }
  }

  if (
    window.location.protocol === 'http:' &&
    !window.location.host.includes('dev.') &&
    !window.location.host.includes('localhost')
  ) {
    // http跳转https
    const httpsUrl = window.location.href.replace('http://', 'https://');
    window.location.href = httpsUrl;
  }

  const queries = Object.fromEntries(
    new URLSearchParams(window.location.search.split('?')?.[1] || ''),
  );

  if (queries?.token) {
    localStorage.setItem('token', queries.token);
  }
  if (queries?.header) {
    localStorage.setItem(
      'NoHeaderModeForH5',
      queries?.header === 'false' ? 'true' : 'false',
    );
  }

  const channel =
    queries.channel ||
    (getEmailLoginMode() ? '4de46be96c7f49938de86c38a1799507' : undefined);

  const initialDataRes = await fetchInitialData({
    // channel: '4cb92e794c744f70b31e2f440f9f937f', // 辉瑞
    channel: channel, // 判断是不是mediflow.ai 的channel_id: 4de46be96c7f49938de86c38a1799507
  });
  const initialData = {
    ...initialDataRes.data.config,
    google_client_id: initialDataRes.data.google_client_id,
    channel_id: initialDataRes.data.channel_id,
  };

  if (initialData.pay_modal) {
    localStorage.setItem('pay_modal', JSON.stringify(initialData.pay_modal));
  }

  // 检查路由为/welcome时，默认弹出登录，并跳转到/home/new
  // localStorage.setItem('third_landing_page', '/guest');
  // localStorage.setItem('third_home_page', '/intro');

  localStorage.setItem('third_landing_page', initialData?.landing_page || '');
  localStorage.setItem('third_home_page', initialData?.home_page || '/home');

  if (initialData?.theme?.style) {
    // 设置全局css变量
    Object.entries(initialData.theme.style).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value as string);
    });
  }

  if (initialData?.theme?.logo) {
    // 监听 theme 并修改 favicon
    const favicon = document.querySelector("link[rel='shortcut icon']");
    if (favicon) {
      favicon.setAttribute('href', initialData.theme.logo);
    }
  }

  if (initialData.default_language) {
    localStorage.setItem('umi_locale', initialData.default_language);
  }

  return initialData;
}

const AppContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState({
    colorPrimary: '#1890ff',
  });

  useEffect(() => {
    const updateTheme = () => {
      setTheme({
        colorPrimary:
          getComputedStyle(document.documentElement)
            .getPropertyValue('--color-primary')
            .trim() || '#1890ff',
      });
    };

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    updateTheme(); // 初次执行一次
    return () => observer.disconnect();
  }, []);

  return (
    <ConfigProvider
      prefixCls="mediflow"
      theme={{
        token: {
          ...theme,
          // colorPrimary: 'var(--color-primary)',
        },
        components: {
          Button: {
            borderRadius: 5,
            controlHeight: 40,
          },
          Popover: {
            borderRadiusLG: 8,
            boxShadowSecondary:
              '0px 24px 48px -12px rgba(11, 14, 28, 0.12), 0px 0px 24px 0px rgba(11, 14, 28, 0.08)',
          },
          Modal: {
            borderRadiusLG: 20,
          },
          Input: {
            boxShadow: 'unset',
          },
          Tooltip: {
            colorBgSpotlight: 'rgba(0, 0, 0, 0.80)',
            paddingSM: 8,
            paddingXS: 10,
          },
        },
      }}
    >
      {children} <Toaster position="bottom-right" />
    </ConfigProvider>
  );
};

export function rootContainer(container: React.ReactNode) {
  return <AppContainer>{container}</AppContainer>;
}
